import { useCallback, useState } from 'react';
import { reverse, sortBy, toNumber } from 'lodash';
import type { Column, SortDirection } from '../types';

// 排序策略类型. 优先级: sortingFn > external(onSort) > builtIn
export type SortStrategy = 'sortingFn' | 'external' | 'builtIn' | undefined;

// 排序配置接口
export interface SortConfig<R, SR> {
  columnKey: string;
  direction: SortDirection;
  column?: Column<R, SR>;
  enableBuiltInSort?: boolean;
}

// 排序执行器接口
export interface SortExecutor<R, SR = unknown> {
  (rows: R[], config: SortConfig<R, SR>): R[];
}

// 排序状态管理接口
export interface SortState {
  columnKey: string;
  direction: SortDirection;
  onSort?: (columnKey: string, direction: SortDirection) => void;
  enableBuiltInSort?: boolean;
}

// 排序Hook的返回值接口
export interface UseSortingReturn<R, SR> {
  sortInfo: SortState;
  setSortInfo: (sortInfo: SortState) => void;
  sortRows: (rows: R[], rawColumns: Column<R, SR>[]) => R[];
  handleSort: (
    columnKey: string,
    direction: SortDirection,
    options?: HandleSortOptions<R, SR>,
  ) => void;
  isExternalSort: (
    column?: Column<R, SR>,
    onSort?: (columnKey: string, direction: SortDirection) => void,
  ) => boolean;
}

// HandleSort 选项接口
export interface HandleSortOptions<R, SR> {
  rawColumns?: Column<R, SR>[];
  enableBuiltInSort?: boolean;
}

/**
 * 自定义排序函数执行器
 */
function executeCustomSort<R, SR = unknown>(rows: R[], config: SortConfig<R, SR>): R[] {
  const { column, direction } = config;

  if (column?.sortingFn) {
    return column.sortingFn(rows, direction);
  }

  return rows;
}

/**
 * 排序Hook
 * 统一管理所有排序相关的逻辑
 */
export function useSorting<R, SR>({
  onSort,
  enableBuiltInSort,
}: {
  onSort?: (columnKey: string, direction: SortDirection) => void;
  enableBuiltInSort?: boolean;
}): UseSortingReturn<R, SR> {
  // 排序状态
  const [sortInfo, setSortInfo] = useState<SortState>({
    columnKey: '',
    direction: 'NONE',
    onSort,
    enableBuiltInSort,
  });

  function determineSortStrategy(
    column?: Column<R, SR>,
    onSort?: (columnKey: string, direction: SortDirection) => void,
  ): SortStrategy {
    if (column?.sortingFn) {
      return 'sortingFn';
    }
    if (onSort) {
      return 'external';
    }
    if (sortInfo.enableBuiltInSort && column?.sortable) {
      return 'builtIn';
    }
    return undefined;
  }

  function isExternalSort(
    column?: Column<R, SR>,
    onSort?: (columnKey: string, direction: SortDirection) => void,
  ): boolean {
    return (column?.sortable && !column?.sortingFn && onSort !== undefined) || false;
  }

  /**
   * 内置排序执行器
   */
  function executeBuiltInSort<R, SR = unknown>(rows: R[], config: SortConfig<R, SR>): R[] {
    const { columnKey, direction, column } = config;
    if (!sortInfo?.enableBuiltInSort) {
      return rows;
    }
    const columnType = column?.dataType ?? 'string';

    let sortedRows = sortBy(rows, (row: R) => {
      const value = row[columnKey as keyof R];
      if (columnType === 'number') {
        return toNumber(value);
      }
      return value;
    });

    if (direction === 'DESC') {
      sortedRows = reverse(sortedRows);
    }

    return sortedRows;
  }

  /**
   * 对行数据进行排序
   * 只处理sortingFn和builtIn两种策略，external策略由外部回调处理
   */
  const sortRows = useCallback(
    (rows: R[], rawColumns: Column<R, SR>[]) => {
      if (!sortInfo.columnKey || sortInfo.direction === 'NONE') {
        return rows;
      }

      const column = rawColumns.find((col) => col.key === sortInfo.columnKey);
      const config: SortConfig<R, SR> = {
        columnKey: sortInfo.columnKey,
        direction: sortInfo.direction,
        column,
      };

      const strategy = determineSortStrategy(column, sortInfo.onSort);

      switch (strategy) {
        case 'sortingFn':
          return executeCustomSort(rows, config);
        case 'builtIn':
          return executeBuiltInSort(rows, config);
        default:
          return rows;
      }
    },
    [sortInfo.columnKey, sortInfo.direction, sortInfo.onSort],
  );

  /**
   * 处理排序事件
   * 这个函数统一处理所有的排序触发逻辑
   */
  const handleSort = useCallback((columnKey: string, direction: SortDirection) => {
    setSortInfo({
      columnKey,
      direction,
      onSort,
      enableBuiltInSort,
    });
  }, []);

  return {
    sortInfo,
    setSortInfo,
    sortRows,
    handleSort,
    isExternalSort,
  };
}

// 导出类型供外部使用
export type { SortDirection, SortInfo } from '../types';
